import React from 'react';
import './WordDisplay.css';

interface WordDisplayProps {
  secretWord: string;
  revealedIndices: number[];
  gameWon?: boolean;
  gameLost?: boolean;
}

function WordDisplay({ secretWord, revealedIndices, gameWon = false, gameLost = false }: WordDisplayProps) {
  // Generate ARIA label for the word display
  const getWordDisplayAriaLabel = (): string => {
    const wordLength = secretWord.length;
    const revealedCount = revealedIndices.length;
    
    if (gameWon) {
      return `Game won! The word was ${secretWord}`;
    } else if (gameLost) {
      return `Game over. The word was ${secretWord}`;
    } else {
      return `Secret word with ${wordLength} letters, ${revealedCount} letters revealed`;
    }
  };

  // Generate ARIA label for each letter box
  const getLetterBoxAriaLabel = (letter: string, index: number): string => {
    const position = index + 1;
    const totalLetters = secretWord.length;
    const isRevealed = revealedIndices.includes(index) || gameWon || gameLost;
    
    if (isRevealed) {
      return `Letter ${position} of ${totalLetters}: revealed ${letter}`;
    } else {
      return `Letter ${position} of ${totalLetters}: hidden`;
    }
  };

  return (
    <div 
      className="word-display"
      role="region"
      aria-label={getWordDisplayAriaLabel()}
      aria-live="polite"
    >
      {secretWord.split('').map((letter, index) => {
        // Show all letters when game is over (win or lose)
        const isRevealed = revealedIndices.includes(index) || gameWon || gameLost;
        return (
          <div 
            key={index} 
            className={`letter-box ${isRevealed ? 'revealed' : ''} 
              ${gameWon ? 'correct-word' : ''} 
              ${gameLost ? 'incorrect-word' : ''}`}
            aria-label={getLetterBoxAriaLabel(letter, index)}
            role="img"
          >
            {isRevealed ? letter : ''}
          </div>
        );
      })}
    </div>
  );
}

export default WordDisplay;
